import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { BookingState } from './model';
import { BOOKING_LOADERS, GROUP_BY_STATUS } from './utils';
import dayjs from 'dayjs';

export const selectBookingState = (state: RootState) => state.bookings;

const selectCurrentCleaner = (state: RootState) =>
  state.cleaners.currentCleaner;

export const selectNewBooking = createSelector(
  selectBookingState,
  ({ newBooking }: BookingState) => newBooking,
);

export const selectBookingLoaders = createSelector(
  selectBookingState,
  ({ loaders }: BookingState) => loaders,
);

export const makeSelectBookingLoader = (key: BOOKING_LOADERS) =>
  createSelector(selectBookingLoaders, (loaders) => loaders[key] ?? false);

export const selectBookings = createSelector(
  selectBookingState,
  ({ bookings }: BookingState) => bookings,
);

export const selectGroupedBookings = createSelector(
  selectBookings,
  (bookings) =>
    bookings?.map(({ status, ...booking }) => ({
      status,
      ...booking,
      group: GROUP_BY_STATUS[status],
    })),
);
export const selectCurrentBooking = createSelector(
  selectBookingState,
  ({ currentBooking }: BookingState) => currentBooking,
);

//TODO -> validate form with zod
export const selectIsBookingValid = createSelector(
  selectBookingState,
  ({ newBooking }: BookingState) =>
    !!newBooking &&
    !!Object.values(newBooking.location ?? {}).length &&
    !Object.values(newBooking.location ?? {}).some((value) => !value) &&
    newBooking.requestDate &&
    !!newBooking.slots.length &&
    !!newBooking.description,
);

export const selectNewBookingDetails = createSelector(
  selectNewBooking,
  selectCurrentCleaner,
  (newBooking, currentCleaner) => {
    if (!newBooking) return null;
    const startTimes = newBooking.slots.map(({ startTime }) => startTime);
    const endTimes = newBooking.slots.map(({ endTime }) => endTime);
    const startTime = Math.min(...startTimes);
    const endTime = Math.max(...endTimes);
    const duration = endTime - startTime;
    return {
      ...newBooking,
      ...newBooking.location,
      startTime,
      endTime,
      date: dayjs(newBooking.requestDate * 1000).format('DD/MM/YYYY'),
      duration: endTime - startTime,
      price: Number(currentCleaner.price * duration).toFixed(2),
      cleaner: {
        firstName: currentCleaner.firstName,
      },
    };
  },
);

export const selectTotalPrice = createSelector(
  selectNewBooking,
  selectCurrentCleaner,
  (newBooking, currentCleaner) => {
    if (!newBooking || !newBooking.slots.length) return 0;
    const startTimes = newBooking.slots.map(({ startTime }) => startTime);
    const endTimes = newBooking.slots.map(({ endTime }) => endTime);
    const startTime = Math.min(...startTimes);
    const endTime = Math.max(...endTimes);
    const duration = endTime - startTime;
    return Number(Number(currentCleaner.price * duration).toFixed(2));
  },
);

export const selectNewBookingData = createSelector(
  selectNewBookingDetails,
  (newBooking) => {
    if (!newBooking) return null;
    const {
      startTime,
      endTime,
      price,
      cleanerId,
      userId,
      location,
      description,
    } = newBooking;

    const reservationPayload = {
      host_id: userId,
      cleaner_id: cleanerId,
      price: Number(price),
      status: 'pending',
      place_id: location.place,
      lat: Number(location.lat),
      lng: Number(location.lng),
      start_time: startTime,
      end_time: endTime,
      description,
    };
    return reservationPayload;
  },
);

export const selectPendignReviewBookings = createSelector(
  selectBookingState,
  (bookings: BookingState) => {
    return bookings.pendingReviewBookings ?? [];
  },
);
